// Intercom messenger: chat + surveys only. https://developers.intercom.com/installing-intercom/web/installation
//
// Identity comes from the server via globalThis.Qwoted (see ViewsShared::IntercomHelper). When
// INTERCOM_USER_ID/INTERCOM_USER_HASH are absent -- logged out, impersonating, or a V2-native user with no V1
// id -- we boot anonymously, which still gets chat and surveys.
//
// Attributes beyond email/name are deliberately NOT sent from here: V1's Sidekiq workers own the
// authoritative Intercom attributes, and a second writer would race them.
import qlog from "../qlog";

const WIDGET_SRC_PREFIX = "https://widget.intercom.io/widget/";

function intercomSettings() {
  const { INTERCOM_APP_ID, INTERCOM_USER_ID, INTERCOM_USER_HASH } = globalThis.Qwoted;

  const settings = {
    api_base: "https://api-iam.intercom.io",
    app_id: INTERCOM_APP_ID,
  };

  if (!INTERCOM_USER_ID || !INTERCOM_USER_HASH) return settings;

  return {
    ...settings,
    user_id: INTERCOM_USER_ID,
    user_hash: INTERCOM_USER_HASH,
    email: globalThis.Qwoted.CURRENT_USER_EMAIL,
    name: globalThis.Qwoted.CURRENT_USER_NAME,
  };
}

// Intercom's published snippet: queue calls made before the widget script lands, then load it.
function installIntercomShim() {
  const existing = window.Intercom;

  if (typeof existing === "function") {
    existing("reattach_activator");
    existing("update", window.intercomSettings);
    return;
  }

  const shim = (...args) => {
    shim.c(args);
  };
  shim.q = [];
  shim.c = (args) => {
    shim.q.push(args);
  };
  window.Intercom = shim;
}

function loadIntercomWidget(appId) {
  const script = document.createElement("script");
  script.async = true;
  script.src = `${WIDGET_SRC_PREFIX}${appId}`;
  script.onerror = () => qlog.warn("Intercom widget script failed to load.");
  document.head.appendChild(script);
}

function initializeIntercom() {
  const appId = globalThis.Qwoted?.INTERCOM_APP_ID;

  if (!appId) {
    qlog.debug("INTERCOM_APP_ID is not configured. Intercom will not be loaded.");
    return;
  }

  window.intercomSettings = intercomSettings();
  installIntercomShim();
  loadIntercomWidget(appId);

  qlog.debug(
    window.intercomSettings.user_id ? "Intercom initialized (identified)." : "Intercom initialized (anonymous).",
  );
}

// Defer past first paint -- the widget is support UI, never on the critical rendering path.
if (document.readyState === "complete") {
  initializeIntercom();
} else {
  window.addEventListener("load", initializeIntercom);
}
